import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, Phone, Car, X, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// TIA TAXI Logo
const TIA_LOGO = 'https://assets.tiataxi.al/shared/logo-header.png';

// Forgot Password Modal Component
const ForgotPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSent(true);
        toast.success('Email u dërgua me sukses!');
      } else {
        toast.error(data.detail || 'Gabim gjatë dërgimit të email-it');
      }
    } catch (error) {
      toast.error('Gabim gjatë lidhjes me serverin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        {sent ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Email u Dërgua!</h3>
            <p className="text-gray-600 text-sm mb-4">
              Kontrolloni emailin tuaj për linkun e rivendosjes së fjalëkalimit.
            </p>
            <p className="text-xs text-gray-400">
              Nëse nuk e gjeni, kontrolloni dosjen "Spam".
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Mbyll
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Rivendos Fjalëkalimin</h3>
            <p className="text-gray-600 text-sm mb-6">
              Shkruani emailin tuaj dhe do t'ju dërgojmë një link për të rivendosur fjalëkalimin.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Emaili juaj"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  'Duke dërguar...'
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Dërgo Linkun
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Load remembered credentials on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberedPassword = localStorage.getItem('rememberedPassword');
    if (rememberedEmail) {
      setFormData({ email: rememberedEmail, password: rememberedPassword || '' });
      setRememberMe(true);
    }
  }, []);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      try {
        const userData = JSON.parse(user);
        const targetPanel = userData.panelType || 'admin';
        navigate(targetPanel === 'dispatcher' ? '/dispatcher' : '/admin', { replace: true });
      } catch (e) {
        // Invalid user data, clear and stay on login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reason') === 'session_expired') {
      toast.error('Sesioni ka skaduar. Ju lutem logohuni përsëri.');
      navigate('/login', { replace: true });
    }
  }, [location.search, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          user_type: formData.email.toLowerCase().includes('admin') ? 'admin' : 'dispatcher'
        }),
      });

      const data = await response.json();

      if (response.ok && data.access_token) {
        // Handle remember me
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
          localStorage.setItem('rememberedPassword', formData.password);
        } else {
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberedPassword');
        }

        // Determine panel type based on email
        const isDispatcher = formData.email.toLowerCase().includes('dispatcher');
        const panelType = isDispatcher ? 'dispatcher' : 'admin';

        // Save token and user info
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify({
          email: formData.email,
          user_type: data.user_type,
          name: data.user_name || data.name || 'User',
          panelType: panelType
        }));

        toast.success('Login me sukses!');
        
        // Redirect based on user type
        setTimeout(() => {
          const targetPath = panelType === 'dispatcher' ? '/dispatcher' : '/admin';
          window.location.href = targetPath;
        }, 100);
      } else {
        toast.error(data.detail || 'Email ose password i gabuar');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Gabim gjatë lidhjes me serverin. Provoni përsëri.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black p-4" data-testid="login-page">
      {/* Login Card */}
      <div className="relative w-full max-w-md">
        {/* Logo above card */}
        <div className="flex justify-center mb-6">
          <img 
            src={TIA_LOGO} 
            alt="TIA TAXI Airport" 
            className="h-16 w-auto"
          />
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8" data-testid="login-form-container">
          <h2 className="text-2xl font-bold text-gray-900 text-left">
            Login
          </h2>
          <hr className="border-gray-200 mt-3 mb-6" />
          
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {/* Email - placeholder only */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                data-testid="login-email-input"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500"
                required
              />
            </div>

            {/* Password - placeholder only */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                data-testid="login-password-input"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500"
                required
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-600">
                  Më mbaj mend
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-gray-500 hover:text-red-600 transition"
              >
                Harruat fjalëkalimin?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              style={{ backgroundColor: '#f30001' }}
              className={`w-full py-3 rounded-xl font-semibold text-white transition shadow-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? 'Duke u identifikuar...' : 'Login'}
            </button>
          </form>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
        )}

        {/* Footer - white text */}
        <div className="text-center mt-6 text-white text-sm">
          © 2026 TIA TAXI Airport. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
